import _ from 'lodash';
import qs from 'query-string';

import routeMap from 'config/routes';
import { ARTIST_PROFILE_VALIDATIONS, ERROR_MESSAGES } from 'config/constants';
import history from 'config/history';
import { displayAlertError } from 'helpers';
import { WIZARD_STEP_IDS } from 'components/Wizard/constants';
import { ARTIST_WIZARD_CONTEXTS } from 'routes/ArtistProfileWizard/helpers';
import { format } from 'date-fns';
import { TableSortDirection } from 'components/Lists/TableTypes';

const { getStepPathForContext } = routeMap.artistProfileWizard;
const { PROMO_POOL } = ARTIST_WIZARD_CONTEXTS;
const { MISSING_SOUNDCLOUD_PROFILE, MISSING_MEMBERS, UNVERIFIED_USERS } =
  ARTIST_PROFILE_VALIDATIONS;

const ORDERED_VALIDATIONS = [
  MISSING_MEMBERS,
  UNVERIFIED_USERS,
  MISSING_SOUNDCLOUD_PROFILE
];

const createWizardUrl = (stepId: string, artistId: string, labelId: string) =>
  `${getStepPathForContext(PROMO_POOL, stepId)}?${qs.stringify({
    artistId,
    labelId,
    backText: encodeURI('Back to Label Subscribers'),
    backPath: history.location.pathname
  })}`;

const WIZARD_STEP_IDS_BY_VALIDATION_ERROR = {
  [MISSING_SOUNDCLOUD_PROFILE]: WIZARD_STEP_IDS.SOUNDCLOUD,
  [MISSING_MEMBERS]: WIZARD_STEP_IDS.ARTIST_MEMBERS,
  [UNVERIFIED_USERS]: WIZARD_STEP_IDS.ARTIST_MEMBERS
};

type VerificationError = {
  message: typeof ERROR_MESSAGES.MISSING_ARTIST_VERIFICATION;
  error: { failed_validations?: string[] };
};

/**
 * [handleAddSubscriberError] - function that checks if artist being added as promo subscriber has missing validations
 * for the promo pool subscriber requirments.
 *
 * NOTE: Currently we don't have a way of adding artists with missing member / user verifications.  In these cases,
 * we just display an error, but soon we will be supporting this by directing users to the promo pool artist wizard
 */

export const handleAddSubscriberError = ({
  error: e,
  artist,
  label,
  isAddAnyPromoArtistActive
}: {
  error: unknown;
  artist: { id: string };
  label: { id: string };
  isAddAnyPromoArtistActive: boolean | undefined;
}) => {
  const errorData = _.get(e, 'response.data', {});
  if (
    'message' in errorData &&
    errorData.message === ERROR_MESSAGES.MISSING_ARTIST_VERIFICATION
  ) {
    const failed_validations =
      (errorData as VerificationError).error.failed_validations || [];

    const { MISSING_MEMBERS, UNVERIFIED_USERS } = ARTIST_PROFILE_VALIDATIONS;

    const validationNotSupportedInBeta =
      !isAddAnyPromoArtistActive &&
      _.intersection(failed_validations, [MISSING_MEMBERS, UNVERIFIED_USERS]).length > 0;

    if (validationNotSupportedInBeta) {
      displayAlertError(
        'Oops! This artist cannot be added to your promo pool yet because their account is not setup.' +
          " You'll be able to invite & setup artists like this after the beta, stay tuned!"
      );
      return;
    }

    // Use to the first missing validation step in the wizard, but ensure they are in the proper order first
    // NOTE: some wizard steps need to be completed before you can precede to the next one
    const validationError = ORDERED_VALIDATIONS.filter(validation =>
      failed_validations.includes(validation)
    )[0];
    const verificationStepId = WIZARD_STEP_IDS_BY_VALIDATION_ERROR[validationError];
    const verificationUrl = createWizardUrl(verificationStepId, artist.id, label.id);
    history.push(verificationUrl);
  } else {
    console.warn(e);
    // Rethrow error for parent component to handle
    throw e;
  }
};

export const getSortValue = (sortId: string, sortDirection: TableSortDirection) =>
  `${sortId}-${sortDirection}`;

export const getArchiveAccessExpirationFormatted = (date: string) =>
  `Until ${format(new Date(date), 'LLL do')}`;

/**
 * [labelSubscriberAverageRating] - returns average rating formatted to nearest tenth decimal place.
 * Also handles returning something other than 0.0 since 0 means they haven't rated, not that they've
 * reacted negatively.
 */

export const labelSubscriberAverageRating = (
  averageRating: number | null,
  noRatingValue?: string
): string => {
  if (!averageRating) return noRatingValue || '-';
  const averageRatingRounded = (Math.round(averageRating * 10) / 10).toFixed(1);

  return averageRatingRounded;
};
